import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { PresenceWebSocket, apiClient } from '../api/client';
import { getAuthToken, getUserRole } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const WS_HEARTBEAT_INTERVAL = 3000;
const HTTP_HEARTBEAT_INTERVAL = 5000;

const GlobalPresenceContext = createContext(null);

export function useGlobalPresence() {
  return useContext(GlobalPresenceContext);
}

export function GlobalPresenceProvider({ children }) {
  const [activeClassId, setActiveClassId] = useState(() => {
    // Restore from localStorage on mount
    const saved = localStorage.getItem('activeClassId');
    return saved ? parseInt(saved) : null;
  });

  const wsRef = useRef(null);
  const wsHeartbeatRef = useRef(null);
  const httpHeartbeatRef = useRef(null);
  const activeClassIdRef = useRef(activeClassId);

  // Keep ref in sync
  useEffect(() => {
    activeClassIdRef.current = activeClassId;
    if (activeClassId) {
      localStorage.setItem('activeClassId', activeClassId.toString());
    }
  }, [activeClassId]);

  // Send beacon leaving (most reliable during page unload)
  const sendBeaconLeaving = useCallback(() => {
    const cid = activeClassIdRef.current;
    if (!cid) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const url = `${API_URL}/classes/${cid}/heartbeat`;
      const data = JSON.stringify({ type: 'leaving' });
      const blob = new Blob([data], { type: 'application/json' });
      navigator.sendBeacon(url + `?token=${token}`, blob);
      console.log('[GlobalPresence] Sent beacon leaving for class', cid);
    } catch (e) {
      console.debug('[GlobalPresence] Beacon failed:', e);
    }
  }, []);

  // HTTP heartbeat
  const sendHttpHeartbeat = useCallback(async (type = 'heartbeat') => {
    const cid = activeClassIdRef.current;
    if (!cid) return;

    try {
      await apiClient.post(`/classes/${cid}/heartbeat`, { type });
      console.log('[GlobalPresence HTTP] Sent', type, 'for class', cid);
    } catch (err) {
      console.debug('[GlobalPresence HTTP] Failed:', err.message);
    }
  }, []);

  // Send leaving message
  const sendLeavingMessage = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.send({ type: 'leaving' });
      } catch (e) {}
    }
    sendHttpHeartbeat('leaving');
    sendBeaconLeaving();
  }, [sendHttpHeartbeat, sendBeaconLeaving]);

  // Connect to class
  const connectToClass = useCallback((classId) => {
    if (!classId) return;

    const role = getUserRole();
    if (role !== 'student') return;

    console.log('[GlobalPresence] Connecting to class:', classId);

    // Disconnect from previous class if different
    if (activeClassIdRef.current && activeClassIdRef.current !== classId) {
      sendLeavingMessage();
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
      }
    }

    setActiveClassId(classId);
    activeClassIdRef.current = classId;

    // Create new WebSocket if not already connected to this class
    if (!wsRef.current) {
      wsRef.current = new PresenceWebSocket(classId, (data) => {
        console.log('[GlobalPresence WS] Got:', data.type);
      });
      wsRef.current.connect();
    }

    // Send initial heartbeat
    sendHttpHeartbeat('heartbeat');
  }, [sendLeavingMessage, sendHttpHeartbeat]);

  // Main effect for heartbeats and event listeners
  useEffect(() => {
    const role = getUserRole();
    if (role !== 'student') return;

    // WS heartbeat interval
    wsHeartbeatRef.current = setInterval(() => {
      if (wsRef.current && activeClassIdRef.current) {
        wsRef.current.send({ type: 'heartbeat' });
      }
    }, WS_HEARTBEAT_INTERVAL);

    // HTTP heartbeat interval
    httpHeartbeatRef.current = setInterval(() => {
      if (activeClassIdRef.current) {
        sendHttpHeartbeat('heartbeat');
      }
    }, HTTP_HEARTBEAT_INTERVAL);

    // Page unload handlers
    const handleUnload = () => sendBeaconLeaving();
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('unload', handleUnload);

    // Tab visibility
    const handleVisibilityChange = () => {
      if (!activeClassIdRef.current) return;

      if (document.hidden) {
        console.log('[GlobalPresence] Tab hidden - sending leaving');
        sendLeavingMessage();
      } else {
        console.log('[GlobalPresence] Tab visible - sending heartbeat');
        sendHttpHeartbeat('heartbeat');
        if (wsRef.current) {
          wsRef.current.send({ type: 'heartbeat' });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (wsHeartbeatRef.current) clearInterval(wsHeartbeatRef.current);
      if (httpHeartbeatRef.current) clearInterval(httpHeartbeatRef.current);
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
      }
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      window.removeEventListener('unload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sendHttpHeartbeat, sendBeaconLeaving, sendLeavingMessage]);

  // Reconnect to saved class on mount (if student)
  useEffect(() => {
    const role = getUserRole();
    if (role === 'student' && activeClassId) {
      console.log('[GlobalPresence] Reconnecting to saved class:', activeClassId);
      connectToClass(activeClassId);
    }
  }, []);

  const value = {
    activeClassId,
    connectToClass,
    sendActivity: () => {
      if (wsRef.current) {
        wsRef.current.send({ type: 'activity' });
      }
      sendHttpHeartbeat('activity');
    }
  };

  return (
    <GlobalPresenceContext.Provider value={value}>
      {children}
    </GlobalPresenceContext.Provider>
  );
}
